import React, { Component } from 'react';
import { getStatus, checkIn, checkOut } from '../../../services/timeTrackingService';
import { getCurrentPosition, GeolocationError } from '../../../util/geoUtils';
import { convertFromUTC } from '../../../util/convert_timezone';

/**
 * TimeClockHeader - Simplified check-in/check-out component for technicians
 *
 * Props:
 * - locationId: The installer location ID
 * - userId: The current user's ID (required for API calls)
 * - onStatusChange: Callback when check-in/out status changes
 */
class TimeClockHeader extends Component {
  state = {
    // Status
    isCheckedIn: false,
    currentSession: null,
    checkOutTime: null, // Store checkout time briefly for display
    loading: true,
    actionLoading: false,

    // Error handling
    error: null,
    errorType: null // 'outside_radius' | 'location_unavailable' | 'permission_denied' | 'rate_limited'
  };

  async componentDidMount() {
    await this.refreshStatus();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.locationId !== this.props.locationId || prevProps.userId !== this.props.userId) {
      this.refreshStatus();
    }
  }

  // Fetch current check-in status from API
  refreshStatus = async () => {
    const { locationId, userId } = this.props;
    if (!locationId || !userId) {
      this.setState({ loading: false });
      return;
    }

    try {
      this.setState({ loading: true, error: null });
      const status = await getStatus(locationId, userId);
      console.log('[TimeClockHeader] getStatus response:', status);
      console.log('[TimeClockHeader] session:', status.session);

      // Check if checked in - either from server flag or by checking session status
      // Active statuses: 'open', 'flagged' (still working, just flagged for review)
      const activeStatuses = ['open', 'flagged'];
      const isCheckedIn = status.is_checked_in ||
        (status.session?.id && activeStatuses.includes(status.session?.status));
      console.log('[TimeClockHeader] isCheckedIn:', isCheckedIn);

      this.setState({
        isCheckedIn,
        currentSession: status.session || null,
        loading: false
      });

      // Notify parent of status
      if (this.props.onStatusChange) {
        this.props.onStatusChange(isCheckedIn, status.session);
      }
    } catch (err) {
      console.error('[TimeClockHeader] Error fetching status:', err);
      this.setState({ loading: false });
    }
  };

  // Handle check-in button click
  handleCheckIn = async () => {
    const { locationId, userId } = this.props;

    try {
      this.setState({ actionLoading: true, error: null, errorType: null });

      // Get GPS position for location validation
      let gpsData = { lat: null, lng: null, accuracy_m: null };
      try {
        gpsData = await getCurrentPosition({ timeout: 15000 });
      } catch (gpsErr) {
        if (gpsErr instanceof GeolocationError && gpsErr.code === 'PERMISSION_DENIED') {
          this.setState({
            actionLoading: false,
            error: 'Location access is turned off.',
            errorType: 'permission_denied'
          });
          return;
        }
        // Other GPS errors - try to check in anyway, server will validate
      }

      // Attempt check-in
      const result = await checkIn({
        location_id: locationId,
        user_id: userId,
        lat: gpsData.lat,
        lng: gpsData.lng,
        accuracy_m: gpsData.accuracy_m,
        device_info: this.getDeviceInfo()
      });

      if (result.success) {
        // Get check-in time from response or use current time as fallback
        const checkInTime = result.check_in_time || result.check_in_at ||
                           result.session?.check_in_time || result.session?.check_in_at ||
                           new Date().toISOString();

        this.setState({
          isCheckedIn: true,
          currentSession: {
            session_id: result.session_id,
            check_in_time: checkInTime,
            check_in_at: checkInTime,
            ...result.session
          },
          checkOutTime: null,
          actionLoading: false,
          error: null
        });

        if (this.props.onStatusChange) {
          this.props.onStatusChange(true, result.session);
        }
      } else {
        this.handleCheckInBlocked(result);
      }
    } catch (err) {
      console.error('[TimeClockHeader] Check-in error:', err);
      const statusCode = err.response?.status;
      const errorData = err.response?.data;

      if (statusCode === 429) {
        this.setState({
          actionLoading: false,
          error: 'Too many attempts. Please wait a minute before trying again.',
          errorType: 'rate_limited'
        });
      } else if (errorData?.reason_code) {
        this.handleCheckInBlocked(errorData);
      } else {
        this.setState({
          actionLoading: false,
          error: "We can't confirm you are on site.",
          errorType: 'location_unavailable'
        });
      }
    }
  };

  // Handle blocked check-in response
  handleCheckInBlocked = (response) => {
    const { reason_code } = response;

    let errorMessage = "We can't confirm you are on site.";
    let errorType = 'location_unavailable';

    switch (reason_code) {
      case 'outside_radius':
        errorMessage = 'You are not at the shop yet.';
        errorType = 'outside_radius';
        break;
      case 'accuracy_too_low':
        errorMessage = "We can't confirm you are on site.";
        errorType = 'location_unavailable';
        break;
      case 'location_not_provided':
        errorMessage = 'Location access is turned off.';
        errorType = 'permission_denied';
        break;
      default:
        break;
    }

    this.setState({
      actionLoading: false,
      error: errorMessage,
      errorType
    });
  };

  // Handle check-out button click - No location validation required
  handleCheckOut = async () => {
    const { locationId, userId } = this.props;

    try {
      this.setState({ actionLoading: true, error: null, errorType: null });

      // Check out - server records timestamp
      const result = await checkOut({
        location_id: locationId,
        user_id: userId,
        client_at: new Date().toISOString()
      });

      if (result.success) {
        const checkOutTime = result.check_out_time || new Date().toISOString();

        this.setState({
          isCheckedIn: false,
          currentSession: null,
          checkOutTime,
          actionLoading: false,
          error: null
        });

        if (this.props.onStatusChange) {
          this.props.onStatusChange(false, null);
        }
      } else {
        this.setState({
          actionLoading: false,
          error: result.message || 'Check-out failed. Please try again.'
        });
      }
    } catch (err) {
      console.error('[TimeClockHeader] Check-out error:', err);
      this.setState({
        actionLoading: false,
        error: 'Check-out failed. Please try again.'
      });
    }
  };

  // Handle "Try again" button
  handleTryAgain = () => {
    this.setState({ error: null, errorType: null });
    this.handleCheckIn();
  };

  // Get device info for logging
  getDeviceInfo = () => {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      timestamp: new Date().toISOString()
    };
  };

  // Format time for display
  formatTime = (timeStr) => {
    if (!timeStr) return '';
    return convertFromUTC(timeStr).format('h:mm A');
  };

  render() {
    const {
      isCheckedIn,
      currentSession,
      checkOutTime,
      loading,
      actionLoading,
      error,
      errorType
    } = this.state;

    // Loading state
    if (loading) {
      return (
        <div className="time-clock-header card mb-4">
          <div className="card-body text-center py-4">
            <div className="spinner-border spinner-border-sm text-secondary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`time-clock-header card mb-4 ${isCheckedIn ? 'checked-in' : ''}`}>
        <div className="card-body">
          {/* Checked In State */}
          {isCheckedIn && (
            <>
              <div className="text-success mb-3">
                <i className="fa-sharp fa-regular fa-check-circle mr-2"></i>
                Checked in at {this.formatTime(currentSession?.check_in_time || currentSession?.check_in_at)}
              </div>

              <button
                className="btn btn-success btn-lg btn-block"
                onClick={this.handleCheckOut}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  'Checking out...'
                ) : (
                  'Check out'
                )}
              </button>
            </>
          )}

          {/* Not Checked In State */}
          {!isCheckedIn && !error && (
            <>
              {/* Show checkout time if just checked out */}
              {checkOutTime && (
                <div className="text-muted mb-3">
                  <i className="fa-sharp fa-regular fa-sign-out mr-2"></i>
                  Checked out at {this.formatTime(checkOutTime)}
                </div>
              )}

              <button
                className="btn btn-primary btn-lg btn-block"
                onClick={this.handleCheckIn}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  'Checking location...'
                ) : (
                  'Check in'
                )}
              </button>
            </>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center">
              <div className="text-danger mb-3">
                <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
                {error}
              </div>

              {errorType !== 'rate_limited' && (
                <button
                  className="btn btn-primary"
                  onClick={this.handleTryAgain}
                  disabled={actionLoading}
                >
                  Try again
                </button>
              )}
            </div>
          )}
        </div>

      </div>
    );
  }
}

export default TimeClockHeader;
